import React, { useEffect, useRef } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from 'lucide-react';
import {
  MONTH_NAMES,
  WEEKDAY_SHORT,
  formatDisplayDate,
  monthWeeks,
  moveFocusDate,
  outOfBounds,
} from '../../lib/dateInput.js';
import { isoDate } from '../../lib/time.js';

// The day a key press just moved to. A move can re-render the month (the
// focused button unmounts) or land in the range picker's other grid, so
// whichever grid renders that day takes focus, not only the one keyed in.
let keyboardTarget = null;

// One month grid, Monday first, shared by DatePicker and DateRangePicker.
// Paint follows the design system's DateTimePicker: a picked day is a cyan
// circle, a range between two picks is a 16% cyan band, today has a steel
// ring, hover is a 14% cyan wash. Keyboard: arrows move a day / a week,
// PageUp/PageDown a month, Enter picks — one roving tab stop per grid.
export function CalendarMonth({
  year,
  month,
  selected,
  range,
  min,
  max,
  focusDate,
  onFocusDate,
  onPick,
  onHover,
  labelledBy,
}) {
  const gridRef = useRef(null);
  const today = isoDate(new Date());
  const weeks = monthWeeks(year, month);

  // Move real focus with the roving tab stop, but only after a key press or
  // while focus is already inside this grid, so opening the popover does not
  // steal it from a field.
  useEffect(() => {
    if (!focusDate || !gridRef.current) return;
    const fromKey = keyboardTarget === focusDate;
    if (!fromKey && !gridRef.current.contains(document.activeElement)) return;
    const button = gridRef.current.querySelector(`[data-date="${focusDate}"]`);
    if (!button) return;
    if (fromKey) keyboardTarget = null;
    button.focus({ preventScroll: true });
  }, [focusDate]);

  function onKeyDown(event) {
    if (
      ![
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'PageUp',
        'PageDown',
      ].includes(event.key)
    )
      return;
    event.preventDefault();
    const from = event.target.getAttribute('data-date') || focusDate;
    if (!from) return;
    keyboardTarget = moveFocusDate(from, event.key);
    onFocusDate(keyboardTarget);
  }

  const firstOfMonth = isoDate(new Date(year, month, 1));
  const rovingDate =
    focusDate && focusDate.slice(0, 7) === firstOfMonth.slice(0, 7)
      ? focusDate
      : null;

  return (
    <div
      ref={gridRef}
      className="cal-grid"
      role="grid"
      aria-labelledby={labelledBy}
      onKeyDown={onKeyDown}
      onMouseLeave={() => onHover?.(null)}
    >
      <div className="cal-row cal-dow" role="row">
        {WEEKDAY_SHORT.map((day, i) => (
          <span
            key={day}
            role="columnheader"
            className={i > 4 ? 'is-weekend' : ''}
            aria-label={day}
          >
            {day}
          </span>
        ))}
      </div>
      {weeks.map((week, w) => (
        <div className="cal-row" role="row" key={w}>
          {week.map((iso, d) => {
            if (!iso)
              return <span key={d} className="cal-cell" role="gridcell" />;
            const disabled = outOfBounds(iso, min, max);
            const isStart = range ? iso === range.from : iso === selected;
            const isEnd = range ? iso === range.to : iso === selected;
            const inBand =
              range &&
              range.from &&
              range.to &&
              iso > range.from &&
              iso < range.to;
            const hasBand =
              range && range.from && range.to && range.from !== range.to;
            const classes = ['cal-cell'];
            if (inBand) classes.push('in-range');
            if (isStart && hasBand) classes.push('range-start');
            if (isEnd && hasBand) classes.push('range-end');
            if (inBand && (d === 0 || iso.endsWith('-01')))
              classes.push('band-left');
            if (inBand && (d === 6 || !week[d + 1])) classes.push('band-right');
            const dayClasses = ['cal-day'];
            if (isStart || isEnd) dayClasses.push('is-picked');
            if (iso === today) dayClasses.push('is-today');
            if (d > 4) dayClasses.push('is-weekend');
            const tabbable = rovingDate
              ? iso === rovingDate
              : iso === firstOfMonth;
            return (
              <span
                key={iso}
                className={classes.join(' ')}
                role="gridcell"
                aria-selected={isStart || isEnd || inBand || undefined}
              >
                <button
                  type="button"
                  className={dayClasses.join(' ')}
                  data-date={iso}
                  tabIndex={tabbable ? 0 : -1}
                  disabled={disabled}
                  aria-label={formatDisplayDate(iso)}
                  aria-current={iso === today ? 'date' : undefined}
                  onClick={() => onPick(iso)}
                  onMouseEnter={() => onHover?.(iso)}
                  onFocus={() => onFocusDate(iso)}
                >
                  {Number(iso.slice(8))}
                </button>
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// "September 2026" with previous / next buttons. Either button may be left
// out (the range picker shows "previous" on its first month and "next" on its
// last). `onTitleClick` turns the title into the year-view toggle.
export function CalendarHeader({
  id,
  year,
  month,
  onPrev,
  onNext,
  onTitleClick,
  yearView = false,
  title,
}) {
  const label = title || `${MONTH_NAMES[month]} ${year}`;
  return (
    <div className="cal-head">
      {onPrev ? (
        <button
          type="button"
          className="cal-nav"
          onClick={onPrev}
          aria-label="Previous"
        >
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
      ) : (
        <span className="cal-nav-spacer" />
      )}
      {onTitleClick ? (
        <button
          type="button"
          id={id}
          className="cal-title is-button"
          onClick={onTitleClick}
          aria-label={yearView ? 'Back to days' : `${label}, choose year`}
        >
          {label}
          {yearView ? (
            <ChevronUp size={16} aria-hidden="true" />
          ) : (
            <ChevronDown size={16} aria-hidden="true" />
          )}
        </button>
      ) : (
        <span id={id} className="cal-title" aria-live="polite">
          {label}
        </span>
      )}
      {onNext ? (
        <button
          type="button"
          className="cal-nav"
          onClick={onNext}
          aria-label="Next"
        >
          <ChevronRight size={20} aria-hidden="true" />
        </button>
      ) : (
        <span className="cal-nav-spacer" />
      )}
    </div>
  );
}

// Twelve years as pills — for a date far from today, where paging month by
// month is hopeless.
export function YearGrid({ firstYear, selectedYear, onPick }) {
  const current = new Date().getFullYear();
  return (
    <div className="cal-years" role="grid">
      {Array.from({ length: 12 }, (_, i) => firstYear + i).map((year) => (
        <button
          key={year}
          type="button"
          className={`cal-year${year === selectedYear ? ' is-picked' : ''}${year === current ? ' is-today' : ''}`}
          onClick={() => onPick(year)}
        >
          {year}
        </button>
      ))}
    </div>
  );
}
